import React, { useEffect, useState, useRef } from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import SponsorsShowcase from '../components/SponsorsShowcase';
import BackButton from '../components/BackButton';
import { Users, Target, GraduationCap, Shield, CheckCircle, Briefcase, ArrowRight, Sparkles, Zap, Building2, Euro, Network, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const useInView = (threshold = 0.1) => {
  const [isInView, setIsInView] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setIsInView(true); }, { threshold });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return [ref, isInView];
};

const OscopGEPage = () => {
  const [heroRef, heroInView] = useInView();
  const [servicesRef, servicesInView] = useInView();
  const [audienceRef, audienceInView] = useInView();
  const [benefitsRef, benefitsInView] = useInView();

  const services = [
    { icon: Users, title: 'Recrutement & mise à disposition', desc: 'Sélection rigoureuse, intégration accompagnée et suivi personnalisé des salariés.', gradient: 'from-blue-500 to-cyan-500' },
    { icon: Target, title: 'Accompagnement RH sur mesure', desc: 'Gestion du temps, des contrats et des obligations sociales pour votre entreprise.', gradient: 'from-green-500 to-emerald-500' },
    { icon: GraduationCap, title: 'Montée en compétences', desc: 'Les salariés bénéficient des formations FORMA\'SCOP pour évoluer.', gradient: 'from-purple-500 to-pink-500' },
    { icon: Shield, title: 'Sécurisation TPE/PME', desc: 'Moins de risques, plus de stabilité RH et de flexibilité pour votre activité.', gradient: 'from-amber-500 to-orange-500' },
  ];

  const audiences = [
    { title: 'TPE / PME', icon: Building2 },
    { title: 'Artisans', icon: Briefcase },
    { title: 'Coopératives', icon: Network },
    { title: 'Collectivités', icon: Building2 },
    { title: 'Associations', icon: Heart },
  ];

  const benefits = [
    { icon: Zap, title: 'Flexibilité de l\'emploi', desc: 'Adaptez vos ressources humaines à votre activité' },
    { icon: Euro, title: 'Réduction des coûts RH', desc: 'Optimisez vos charges tout en gardant la qualité' },
    { icon: GraduationCap, title: 'Salariés formés', desc: 'Bénéficiez de collaborateurs qualifiés' },
    { icon: Heart, title: 'Engagement ESS', desc: 'Participez à une économie solidaire' },
    { icon: Shield, title: 'Sécurisation juridique', desc: 'Conformité et sécurité sociale garanties' },
  ];

  return (
    <div className="min-h-screen bg-slate-950">
      <Navigation />
      
      {/* Hero Section */}
      <section ref={heroRef} className="relative min-h-[80vh] flex items-center overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 pt-20">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1531535807748-218331acbcb4?w=1920&q=80" 
            alt="HR team collaboration" 
            className="w-full h-full object-cover opacity-15"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/80 via-slate-900/90 to-slate-950" />
        </div>
        
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 -left-32 w-[600px] h-[600px] bg-gradient-to-r from-blue-600/20 to-cyan-600/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 -right-32 w-[500px] h-[500px] bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}} />
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:60px_60px]" />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 py-20">
          <BackButton to="/" label="Retour à l'accueil" className="text-white/70 mb-8 hover:text-white transition-colors" />
          
          <div className={`transition-all duration-1000 ${heroInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <Badge className="bg-gradient-to-r from-blue-500/20 to-cyan-500/20 text-blue-300 border border-blue-500/30 mb-6 px-4 py-2">
              <Users className="w-4 h-4 mr-2" /> Groupement d'Employeurs
            </Badge>
            
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black mb-6">
              <span className="bg-gradient-to-r from-white via-blue-100 to-white bg-clip-text text-transparent">O'SCOP GE</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-slate-300 max-w-4xl mb-4 leading-relaxed font-semibold">
              Mutualiser l'emploi. Sécuriser les entreprises. Accompagner les talents.
            </p>
            
            <p className="text-lg text-slate-400 max-w-3xl mb-10 leading-relaxed">
              Le Groupement d'Employeurs coopératif de l'écosystème <span className="text-blue-400">O'SCOP</span> pour les entreprises ultramarines.
            </p>

            <div className="flex flex-wrap gap-4">
              <Button size="lg" className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-bold px-8 py-6 text-lg shadow-2xl shadow-blue-500/25 hover:shadow-blue-500/40 transition-all duration-300 hover:scale-105" onClick={() => window.location.href = '/contact'}>
                Nous contacter <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button size="lg" variant="outline" className="border-2 border-white/20 text-white hover:bg-white/10 px-8 py-6 text-lg transition-all duration-300" onClick={() => { window.location.href = 'https://oscopge.objectifscopoutremer.com'; }} data-testid="oscop-ge-devenir-membre-btn">
                Devenir membre
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Role Section */}
      <section className="py-16 bg-gradient-to-b from-slate-950 to-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-slate-800/30 backdrop-blur-xl rounded-3xl p-8 md:p-12 border border-slate-700/50">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-lg">
                <Target className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-white">Notre rôle</h2>
            </div>
            <p className="text-lg text-slate-300 mb-6 leading-relaxed">
              <span className="text-blue-400 font-semibold">O'SCOP GE</span> permet aux entreprises ultramarines :
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {['Partager un salarié entre plusieurs structures', 'Accéder à des compétences autrement inaccessibles', 'Sécuriser leurs recrutements', 'Réduire leurs charges RH', 'Accompagner les salariés dans un parcours durable'].map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span className="text-slate-300">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section ref={servicesRef} className="py-24 bg-slate-900 relative overflow-hidden">
        <div className="absolute inset-0"><div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] bg-gradient-to-b from-blue-600/10 to-transparent rounded-full blur-3xl" /></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className={`text-center mb-16 transition-all duration-1000 ${servicesInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <Badge className="bg-blue-500/20 text-blue-300 border border-blue-500/30 mb-4 px-4 py-2"><Sparkles className="w-4 h-4 mr-2" /> Nos services</Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Un accompagnement <span className="text-blue-400">complet</span></h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <div key={index} className={`group bg-slate-800/30 backdrop-blur-xl rounded-2xl p-8 border border-slate-700/50 hover:border-slate-600 transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl ${servicesInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{ transitionDelay: `${index * 100}ms` }}>
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${service.gradient} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg`}>
                  <service.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{service.title}</h3>
                <p className="text-slate-400 leading-relaxed">{service.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Audience Section */}
      <section ref={audienceRef} className="py-24 bg-gradient-to-b from-slate-900 to-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-16 transition-all duration-1000 ${audienceInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <Badge className="bg-green-500/20 text-green-300 border border-green-500/30 mb-4 px-4 py-2"><Users className="w-4 h-4 mr-2" /> Pour qui ?</Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Nos <span className="text-green-400">bénéficiaires</span></h2>
          </div>

          <div className="flex flex-wrap justify-center gap-4">
            {audiences.map((audience, index) => (
              <div key={index} className={`group flex items-center gap-3 bg-slate-800/50 backdrop-blur-xl px-6 py-4 rounded-2xl border border-slate-700/50 hover:border-green-500/50 transition-all duration-300 hover:-translate-y-1 ${audienceInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{ transitionDelay: `${index * 100}ms` }}>
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500/20 to-emerald-500/20 flex items-center justify-center border border-green-500/30 group-hover:scale-110 transition-transform">
                  <audience.icon className="w-5 h-5 text-green-400" />
                </div>
                <span className="text-white font-medium">{audience.title}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section ref={benefitsRef} className="py-24 bg-gradient-to-br from-blue-600 via-cyan-600 to-blue-700 relative overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.1)_1px,transparent_1px)] bg-[size:40px_40px]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Les <span className="text-blue-200">avantages</span></h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className={`bg-white/10 backdrop-blur-xl rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all duration-300 hover:-translate-y-2 text-center ${benefitsInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{ transitionDelay: `${index * 100}ms` }}>
                <div className="w-14 h-14 mx-auto rounded-2xl bg-white/20 flex items-center justify-center mb-4">
                  <benefit.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{benefit.title}</h3>
                <p className="text-sm text-white/70">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-slate-950 relative">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Prêt à <span className="text-blue-400">mutualiser</span> l'emploi ?</h2>
          <p className="text-xl text-slate-400 mb-10">Contactez-nous pour découvrir comment O'SCOP GE peut accompagner votre entreprise.</p>
          <Button size="lg" className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-bold px-10 py-6 text-lg shadow-2xl shadow-blue-500/25 hover:shadow-blue-500/40 transition-all duration-300 hover:scale-105" onClick={() => window.location.href = '/contact'}>
            Nous contacter <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      <SponsorsShowcase variant="branch" branche="OSCOP GE" />
      <Footer />
    </div>
  );
};

export default OscopGEPage;
